import { Avatar, Stack, Typography } from "@mui/material";
import { usePayPointOwnerQuery } from "Api/Queries/PayPoints/usePayPointOwnerQuery";
import PaypointImage from "Assets/Images/Images/moone-paypoint.svg";
import { getApiUrl } from "Infrastructure/Api/Api";
import { getContextProfilePhotoUrl } from "Utils/Image/getProfilePhotoUrl";

type Size = "medium" | "large";

const STYLES: Record<
  Size,
  {
    avatarSize: number;
    gap: number;
    fontSize: number;
    lineHeight: string;
  }
> = {
  medium: {
    avatarSize: 72,
    gap: 1,
    fontSize: 16,
    lineHeight: "24px",
  },
  large: {
    avatarSize: 120,
    gap: 2,
    fontSize: 22,
    lineHeight: "32px",
  },
};

type Props = {
  creditorName?: string | null;
  payPointPublicID?: string | null;
  /** Founder Merchant persona whose avatar is the merchant's image (ZS-1607). */
  merchantIdentityUserPublicID?: string | null;
  merchantProfilePicture?: string | null;
  size?: Size;
};

export const MerchantHeader = ({
  creditorName,
  payPointPublicID,
  merchantIdentityUserPublicID,
  merchantProfilePicture,
  size = "medium",
}: Props) => {
  const { avatarSize, gap, fontSize, lineHeight } = STYLES[size];
  const { data: ownerData } = usePayPointOwnerQuery(payPointPublicID);

  // Pay point: facility logo first, otherwise the owning merchant's avatar. Without a pay point
  // the transaction's own merchant avatar is all there is.
  const payPointImageUrl = ownerData?.logoUrl
    ? `${getApiUrl()}${ownerData.logoUrl}`
    : getContextProfilePhotoUrl(
        ownerData?.merchantIdentityUserPublicID,
        ownerData?.merchantProfilePicture,
      );

  const merchantAvatarUrl = getContextProfilePhotoUrl(
    merchantIdentityUserPublicID,
    merchantProfilePicture,
  );

  const imageUrl = payPointPublicID ? payPointImageUrl : merchantAvatarUrl;
  // Variable Collector entry pages don't have a transaction (and therefore no creditorName)
  // yet — fall back to the paypoint owner's name from the same query that fetched the image.
  const displayName = creditorName ?? ownerData?.name;

  return (
    <Stack alignItems="center" gap={gap}>
      <Avatar
        src={imageUrl}
        variant="rounded"
        sx={{ width: avatarSize, height: avatarSize, bgcolor: "transparent" }}
      >
        <img src={PaypointImage} width="100%" alt="Moone paypoint" />
      </Avatar>
      {displayName && (
        <Typography
          fontSize={fontSize}
          fontWeight={500}
          lineHeight={lineHeight}
          color="#353539"
          textAlign="center"
        >
          {displayName}
        </Typography>
      )}
    </Stack>
  );
};
